import { Disc3, Send, Radio, DollarSign, CheckCircle2, XCircle, MessageSquare, LifeBuoy } from "lucide-react";

/**
 * Vertical activity timeline. `events` shape:
 *   { kind: 'release'|'withdrawal'|'ticket', event: string, when: iso, title, amount, status, ... }
 */

const KIND = {
  release_created:   { icon: Disc3,        color: "text-blue-300",    bg: "bg-blue-500/10",    label: "New draft" },
  release_submitted: { icon: Send,         color: "text-amber-300",   bg: "bg-amber-500/10",   label: "Submitted for review" },
  release_live:      { icon: Radio,        color: "text-emerald-300", bg: "bg-emerald-500/10", label: "Live on stores" },
  withdrawal_requested: { icon: DollarSign, color: "text-blue-300",   bg: "bg-blue-500/10",    label: "Withdrawal requested" },
  withdrawal_paid:      { icon: CheckCircle2, color: "text-emerald-300", bg: "bg-emerald-500/10", label: "Withdrawal paid" },
  withdrawal_rejected:  { icon: XCircle,     color: "text-red-300",     bg: "bg-red-500/10",     label: "Withdrawal declined" },
  ticket_opened:     { icon: LifeBuoy,     color: "text-blue-300",    bg: "bg-blue-500/10",    label: "Support ticket opened" },
  ticket_reply:      { icon: MessageSquare, color: "text-emerald-300", bg: "bg-emerald-500/10", label: "Noizbloc replied" },
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en", { month: "short", day: "numeric" });
}

function keyFor(e) {
  if (e.kind === "release") return `release_${e.event}`;
  if (e.kind === "withdrawal") return `withdrawal_${e.event}`;
  if (e.kind === "ticket") return `ticket_${e.event}`;
  return "release_created";
}

export default function ActivityFeed({ events = [], goto }) {
  return (
    <div
      data-testid="activity-feed"
      className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 md:p-6 flex flex-col"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-500">Activity</div>
          <h3 className="mt-1 font-display text-xl text-white">What&apos;s been happening</h3>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed border-white/10 py-10 px-4 text-center text-sm text-slate-500">
          Nothing yet — create a release or open a support ticket and it&apos;ll show up here.
        </div>
      ) : (
        <ol className="mt-5 relative border-l border-white/10 space-y-4 pl-5">
          {events.map((e, i) => {
            const meta = KIND[keyFor(e)] || KIND.release_created;
            const Icon = meta.icon;
            const target = e.kind === "ticket" ? "support" : e.kind === "withdrawal" ? "royalties" : "releases";
            return (
              <li key={`${e.kind}-${e.event}-${e.when}-${i}`} className="relative" data-testid={`activity-item-${i}`}>
                <span className={`absolute -left-[27px] top-1 h-4 w-4 rounded-full ${meta.bg} flex items-center justify-center border border-white/10`}>
                  <Icon size={9} className={meta.color} />
                </span>
                <button
                  type="button"
                  onClick={() => typeof goto === "function" && goto(target)}
                  className="w-full text-left group"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-[11px] tracking-[0.18em] uppercase font-medium text-slate-500 group-hover:text-white transition-colors">
                      {meta.label}
                    </div>
                    <div className="text-[11px] text-slate-500">{relTime(e.when)}</div>
                  </div>
                  <div className="mt-0.5 text-sm text-white truncate">
                    {e.kind === "withdrawal"
                      ? `$${(e.amount || 0).toFixed(2)} · #${(e.id || "").slice(-6).toUpperCase() || "—"}`
                      : e.title}
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
